import { FormControl, FormErrorMessage, Flex, Grid } from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import Input from "../inputs/Input";
import { forwardRef, useImperativeHandle } from "react";
import type { VariantOption } from "../../types/earmold.types";
import FileImageInput from "../FileImageInput";
import { useFile } from "../../hooks/file.hooks";

type VariantOptionFormProps = {
	value?: VariantOption;
	asImageInput?: boolean;
};

export type VariantOptionFormHandler = {
	values: () => Partial<VariantOption>;
	valid: () => Promise<boolean>;
};

const VariantOptionForm = forwardRef<
	VariantOptionFormHandler,
	VariantOptionFormProps
>(({ value, asImageInput = false }: VariantOptionFormProps, ref) => {
	const methods = useForm();
	const { data: file } = useFile({ id: value?.fileId });

	useImperativeHandle(ref, () => ({
		valid: () => {
			return methods.trigger();
		},
		values: () => {
			const values = methods.getValues();

			return {
				label: values.label,
				cost: Number.parseInt(values.cost as string),
				sageId: values.sageId,
				description: values.description ?? "",
				file: values.file ?? null,
			};
		},
	}));
	return (
		<FormProvider {...methods}>
			<form style={{ marginTop: "32px", marginBottom: "32px" }}>
				<Flex direction="column" gap="18px">
					{asImageInput && (
						<FormControl
							isInvalid={!!methods.formState.errors.file}
							className="flex justify-center"
						>
							<div className="flex flex-col items-center">
								<FileImageInput
									defaultValue={file}
									onFileChange={(files: File[] | null) => {
										if (!files || files.length === 0) {
											methods.setValue("file", null);
											return;
										}

										methods.setValue("file", files[0]);
									}}
									{...methods.register("file", {
										required: "Ce champ est obligatoire",
									})}
								></FileImageInput>
								<FormErrorMessage>
									<>{methods.formState.errors?.file?.message}</>
								</FormErrorMessage>
							</div>
						</FormControl>
					)}
					<FormControl isInvalid={!!methods.formState.errors.label}>
						<Input
							id="label"
							placeholder="Valeur"
							defaultValue={value?.label}
							{...methods.register("label", {
								required: "Ce champ est obligatoire",
							})}
						></Input>
						<FormErrorMessage>
							<>{methods.formState.errors?.label?.message}</>
						</FormErrorMessage>
					</FormControl>
					<Grid gridTemplateColumns="3fr 1fr" gap="8px">
						<FormControl isInvalid={!!methods.formState.errors.label}>
							<Input
								id="sageId"
								placeholder="ID Sage"
								defaultValue={value?.sageId}
								{...methods.register("sageId")}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.sageId?.message}</>
							</FormErrorMessage>
						</FormControl>
						<FormControl isInvalid={!!methods.formState.errors.label}>
							<Input
								id="cost"
								placeholder="Tarif"
								unit="€"
								defaultValue={value?.cost}
								{...methods.register("cost")}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.cost?.message}</>
							</FormErrorMessage>
						</FormControl>
					</Grid>
					<FormControl isInvalid={!!methods.formState.errors.description}>
						<Input
							id="description"
							placeholder="Texte explicatif (facultatif)"
							defaultValue={value?.description}
							{...methods.register("description")}
						></Input>
						<FormErrorMessage>
							<>{methods.formState.errors?.description?.message}</>
						</FormErrorMessage>
					</FormControl>
				</Flex>
			</form>
		</FormProvider>
	);
});

export default VariantOptionForm;
